import { handleCheckUsernameReq } from "../../redux/user-slices/auth.slice";
import { userRegistrationReq } from "../../redux/member-slices/auth.slice";
import FacebookLogin from "../../components/ui/FacebookLogin";
import SkeletonImage from "../../components/ui/SkeletonImage";
import GoogleLogin from "../../components/ui/GoogleLogin";
import InputFieldA from "../../components/ui/InputFieldA";
import { signupSchema } from "../../utils/velidations";
import { useDispatch, useSelector } from "react-redux";
import { Link as NavLink } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import Images from "../../assets/js/Images";
import Icons from "../../assets/js/Icons";
import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid2 as Grid,
  Icon,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

function Signup() {
  const dispatch = useDispatch();
  const [password, setPassword] = useState(false);
  const [helperText, setHelperText] = useState([]);
  const [usernameError, setUsernameError] = useState(null);
  const [usernameSuccess, setUsernameSuccess] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const { other } = useSelector((state) => state.memberSlice);
  const displayUsernameError = helperText?.username?.[0] || usernameError;

  // handle validate username
  let interval = null;
  const validateUsername = (e) => {
    const value = e.target?.value;
    const allowedChars = /[^a-z0-9_]/g;
    const filteredValue = value.replace(allowedChars, "");
    e.target.value = filteredValue;
    clearTimeout(interval);
    setUsernameError(null);
    setUsernameSuccess(null);
    if (filteredValue) {
      setIsCheckingUsername(true);
      interval = setTimeout(() => {
        dispatch(handleCheckUsernameReq({ username: filteredValue }))
          .unwrap()
          .then((res) => {
            setUsernameSuccess(res?.message || "Username available!");
            setUsernameError(null);
            setIsCheckingUsername(false);
          })
          .catch((err) => {
            const errorMsg = err?.response?.data?.error || "Username already taken!";
            setUsernameError(errorMsg);
            setUsernameSuccess(null);
            setIsCheckingUsername(false);
          });
      }, 500);
    } else {
      setIsCheckingUsername(false);
      setUsernameError("Username is required");
    }
  };

  const handleUsernameBlur = (e) => {
    const value = e?.target?.value;
    if (!value || !value.trim()) {
      setUsernameError("Username is required");
      setUsernameSuccess(null);
    }
  };

  // Handle user signup
  const handleSubmit = (e) => {
    e.preventDefault();

    const form = new FormData(e.target);
    const formData = Object.fromEntries(form);

    if (!formData.username || !formData.username.trim()) {
      setUsernameError("Username is required");
      setHelperText({ username: ["Username is required"] });
      return;
    }

    // Prevent submission if username is being checked or has error
    if (isCheckingUsername) {
      setHelperText({ username: ["Please wait, checking username availability..."] });
      return;
    }
    if (usernameError) {
      setHelperText({ username: [usernameError] });
      return;
    }

    const result = signupSchema.safeParse(formData);
    if (!result.success) {
      const groupedErrors = {};
      result.error.issues.forEach((issue) => {
        const key = issue.path[0];
        if (!groupedErrors[key]) {
          groupedErrors[key] = [];
        }
        groupedErrors[key].push(issue.message);
      });
      setHelperText(groupedErrors);
      return;
    }
    setHelperText([]);
    dispatch(userRegistrationReq(formData));
  };

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth={false} fixed>
        <Grid container spacing={3} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>
            <Box>
              <SkeletonImage src={Images?.login} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>
            <Paper
              sx={{
                p: 3,
                border: "1px solid #f0f0f0",
              }}
              component="form"
              onSubmit={handleSubmit}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  mb={3}
                  flexGrow={1}
                  fontSize={18}
                  color="other.color1"
                  fontWeight="500"
                >
                  Member Register 
                </Typography>
                <Link
                  sx={{
                    mb: "20px",
                    fontSize: "15px",
                    fontWeight: "500",
                  }}
                  underline="hover"
                  component={NavLink}
                  to="/specialist-registration"
                >
                  Are you a Specialist?
                </Link>
              </Box>

              <Stack spacing={3}>
                <InputFieldA
                  label="Name"
                  name="fullName"
                  variant="filled"
                  varianttype="login"
                  helperText={helperText}
                />
                <Box>
                  <InputFieldA
                    name="username"
                    label="Username"
                    variant="filled"
                    varianttype="login"
                    error={Boolean(displayUsernameError)}
                    helperText={displayUsernameError}
                    onChange={validateUsername}
                    onBlur={handleUsernameBlur}
                    slotProps={{
                      input: {
                        endAdornment: isCheckingUsername ? (
                          <CircularProgress size={20} thickness={6} />
                        ) : usernameSuccess && !displayUsernameError ? (
                          <Tooltip title="Username is available">
                            <Icon sx={{ fontSize: "22px", color: "success.main" }}>
                              <Icons.Checkmark />
                            </Icon>
                          </Tooltip>
                        ) : displayUsernameError ? (
                          <Tooltip title={displayUsernameError}>
                            <Icon sx={{ fontSize: "22px", color: "error.main", cursor: "help" }}>
                              <Icons.Alert />
                            </Icon>
                          </Tooltip>
                        ) : null,
                      },
                    }}
                    sx={{ width: "100%" }}
                  />
                  {usernameSuccess && !displayUsernameError && (
                    <Typography mt={0.5} fontSize="14px" color="success.main">
                      {usernameSuccess}
                    </Typography>
                  )}
                </Box>
                <InputFieldA
                  name="email"
                  type="email"
                  label="Email"
                  helperText={helperText}
                />
                <InputFieldA
                  name="password"
                  type="password"
                  label="Create Password"
                  helperText={helperText}
                  onFocus={() => {
                    setPassword(true);
                  }}
                  onBlur={() => {
                    setPassword(false);
                  }}
                />
                {password && (
                  <Typography mt={0.5} fontSize="14px" color="warning.main">
                    Password must be 8-16 characters long and include at least
                    one uppercase letter, one lowercase letter, one digit, and
                    one special character (!@#$%^&*).
                  </Typography>
                )}
                <InputFieldA
                  type="password"
                  name="confPassword"
                  label="Confirm Password"
                  helperText={helperText}
                  onFocus={() => {
                    setPassword(true);
                  }}
                  onBlur={() => {
                    setPassword(false);
                  }}
                />
                <Box>
                  <ReCAPTCHA sitekey={process.env.REACT_APP_CAPTCHA_KEY} />
                  {helperText?.["g-recaptcha-response"] && (
                    <Typography mt={0.5} fontSize="14px" color="error.main">
                      {helperText?.["g-recaptcha-response"]}
                    </Typography>
                  )}
                  <Typography
                    mt={1}
                    fontSize={14}
                    color="other.color1"
                    textAlign="end"
                  >
                    <Link to="/login" underline="hover" component={NavLink}>
                      Already have an account?
                    </Link>
                  </Typography>
                </Box>
                 <Button
                   sx={{
                     p: "9px 17px",
                     lineHeight: "1.5",
                     fontSize: "18px",
                     justifyContent: "center",
                   }}
                   type="submit"
                   variant="contained"
                   disabled={isCheckingUsername || Boolean(usernameError) || other?.loadingLogin}
                   endIcon={
                     other?.loadingLogin && (
                       <CircularProgress size={22} color="text" thickness={6} />
                     )
                   }
                 >
                  Signup
                </Button>
                <Divider>
                  <Typography sx={{ color: "#d4d4d4" }} component="span">
                    Or
                  </Typography>
                </Divider>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FacebookLogin />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <GoogleLogin />
                  </Grid>
                </Grid>
                <Typography mb={3} color="other.color1" textAlign="center">
                  By continuing you indicate that you agree to Quora's to
                  InnerPals
                  <Link
                    sx={{
                      mb: "20px",
                      whiteSpace: "nowrap",
                    }}
                    underline="hover"
                    component={NavLink}
                    to="/terms-and-conditions"
                  >
                    {" "}
                    Terms of Service
                  </Link>{" "}
                  and
                  <Link
                    sx={{
                      mb: "20px",
                      whiteSpace: "nowrap",
                    }}
                    underline="hover"
                    component={NavLink}
                    to="/privacy-policy"
                  >
                    {" "}
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Signup;
